import { ArrowsClockwiseIcon } from '@phosphor-icons/react';
import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';

import { ICON_WEIGHT } from '@/components/icon';
import { InfoTip } from '@/components/info-tip';
import { Button } from '@/components/ui/button';
// Deep imports, not the barrel: `features/sources`' own barrel pulls in the
// whole search panel, and this section needs the candidate list, one hook and
// one pure helper.
import { CandidateList } from '@/features/sources/CandidateList';
import { preferredSource } from '@/features/sources/credentials';
import { useTrackSource } from '@/features/sources/use-sources';
import { aircraft as aircraftRepo, settings } from '@/lib/db';
import { formatLocalDate, formatLocalTime } from '@/lib/format';
import { daysSpanned } from '@/lib/sources';

import {
  FIRST_CHECK_LOOKBACK_DAYS,
  MAX_LOOKBACK_DAYS,
  MIN_CHECK_INTERVAL_HOURS,
  readAutoImportConfig,
  searchWindow,
} from './settings';
import { setAutoImportEnabled, setDefaultAircraft, useAutoImportStore } from './use-auto-import';

/**
 * Picking a default aircraft, and turning the startup check on.
 *
 * Everything a pilot needs to decide is on this card before the tick, not
 * after it: which source the check will use, what one search of it costs in
 * that source's own words, and how far back it reaches. `ApiKeysSection`'s rule
 * — terms next to the control, never behind a link — applied to the one thing in
 * this app that reaches for a network on its own.
 *
 * What the check finds is listed right here, under the button, with the same
 * per-row Fetch as the fetch screen: a pilot who just pressed "Check now" is
 * looking at this card, and sending them to another screen to act on the answer
 * is a navigation for nothing.
 *
 * The check rides on the same preferred source the logbook's "find this
 * flight" button uses, chosen on the card below this one. One preference, not
 * two: a pilot who has decided which source they trust has not decided it
 * twice.
 */

const SELECT_CLASS =
  'border-border bg-surface rounded-control border px-2.5 py-1.5 text-sm outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring';

export function AutoImportSection() {
  const rows = useLiveQuery(() => settings.getAll(), [], null);
  const aircraftList = useLiveQuery(() => aircraftRepo.list(), [], null);
  const check = useAutoImportStore((state) => state.check);
  const checking = useAutoImportStore((state) => state.status) === 'checking';
  const run = useAutoImportStore((state) => state.run);

  /*
   * Built by the same hook the fetch screen uses, so it is one adapter across
   * renders rather than a new one each time: the ADS-B adapter caches the day
   * file it downloaded, and a fresh instance per render would throw that away
   * between the list appearing and the pilot pressing a button on it.
   */
  const descriptor = rows === null ? undefined : preferredSource(rows);
  const source = useTrackSource(descriptor);

  if (rows === null || aircraftList === null) {
    return null;
  }

  const config = readAutoImportConfig(rows);
  const chosen = aircraftList.find((entry) => entry.id === config.aircraftId);
  const window = searchWindow(config.lastCheckedAt, new Date());
  const estimate =
    source?.estimateCost?.('search', daysSpanned(window.from, window.to)) ?? undefined;
  const needsHex =
    descriptor?.lookupBy === 'icao-hex' && chosen !== undefined && chosen.icaoHex === undefined;

  return (
    <section className="border-border bg-surface rounded-container border p-4">
      <h2 className="text-sm font-semibold tracking-tight">Default aircraft</h2>
      <p className="text-muted-foreground mt-1 text-sm leading-relaxed">
        Checked for new flights on startup.
      </p>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-muted-foreground">Aircraft</span>
          <select
            className={SELECT_CLASS}
            value={config.aircraftId ?? ''}
            onChange={(e) => {
              void setDefaultAircraft(e.target.value === '' ? undefined : e.target.value);
            }}
          >
            <option value="">None</option>
            {aircraftList.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.registration}
              </option>
            ))}
          </select>
        </label>
      </div>

      {aircraftList.length === 0 ? (
        <p className="text-muted-foreground mt-2 text-xs leading-relaxed">
          No aircraft.{' '}
          <Link to="/aircraft/new" className="underline underline-offset-2">
            Add an aircraft
          </Link>{' '}
          to set a default.
        </p>
      ) : null}

      <label className="mt-3 flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="accent-accent mt-0.5 size-4"
          checked={config.enabled}
          disabled={config.aircraftId === undefined}
          onChange={(e) => {
            void setAutoImportEnabled(e.target.checked);
          }}
        />
        <span>
          Check for new flights on startup
          <span className="text-muted-foreground block text-xs leading-relaxed">
            Results are listed for selection. Nothing is downloaded automatically. Results cover the
            aircraft, including flights by other pilots.
          </span>
        </span>
      </label>

      <p className="text-muted-foreground mt-3 flex flex-wrap items-center gap-x-1 text-xs">
        <span>
          {descriptor === undefined ? 'No data source set up.' : `Source: ${descriptor.label}.`}{' '}
          {config.lastCheckedAt === null
            ? 'Never checked.'
            : `Last checked ${formatLocalDate(config.lastCheckedAt)} ${formatLocalTime(
                config.lastCheckedAt,
              )}.`}
        </span>
        <InfoTip label="About the startup check">
          <p>
            Uses the preferred source set under Flight data sources.
            {estimate === undefined ? null : (
              <>
                {' '}
                {estimate.credits > 0
                  ? `About ${String(estimate.credits)} ${estimate.credits === 1 ? 'credit' : 'credits'} per check. `
                  : ''}
                {estimate.description}
              </>
            )}
          </p>
          <p>
            At most one search every {MIN_CHECK_INTERVAL_HOURS} hours. The first check looks back{' '}
            {FIRST_CHECK_LOOKBACK_DAYS} days; later checks cover the time since the last, up to{' '}
            {MAX_LOOKBACK_DAYS} days.
          </p>
        </InfoTip>
      </p>

      {needsHex ? (
        <p className="text-muted-foreground mt-2 text-xs leading-relaxed">
          {chosen.registration} has no ICAO 24-bit address on file. {descriptor.label} needs one;
          the CAA register is checked when the search runs.
        </p>
      ) : null}

      <div className="mt-3">
        <Button
          size="sm"
          variant="outline"
          disabled={checking || config.aircraftId === undefined}
          onClick={() => {
            void check({ force: true });
          }}
        >
          <ArrowsClockwiseIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
          {checking ? 'Checking…' : 'Check now'}
        </Button>
      </div>

      {run === undefined || run.status === 'skipped' ? null : (
        <div className="mt-3">
          {run.status === 'failed' ? (
            <p role="alert" className="text-danger text-xs leading-relaxed">
              {run.message ?? 'That check did not finish.'}
            </p>
          ) : run.found.length === 0 ? (
            <p className="text-muted-foreground text-xs leading-relaxed">
              Nothing new on {run.sourceLabel ?? 'that source'} for{' '}
              {run.registration ?? 'the default aircraft'}.
            </p>
          ) : (
            <CandidateList
              candidates={run.found}
              source={source}
              estimate={source?.estimateCost?.('fetch', 1) ?? null}
              heading={`${String(run.found.length)} ${
                run.found.length === 1 ? 'flight' : 'flights'
              } of ${run.registration ?? 'the default aircraft'} not yet imported. Fetch the ones flown.`}
            />
          )}
        </div>
      )}
    </section>
  );
}
